<template>
  <v-navigation-drawer
    :key="isMobileLayout ? 'mobile' : 'desktop'"
    app
    :model-value="drawerVisible"
    :temporary="isMobileLayout"
    :permanent="!isMobileLayout"
    :rail="!isMobileLayout && navCollapsed"
    :width="200"
    :rail-width="72"
    class="sideNav"
    :color="themeColor"
    @update:model-value="onDrawerUpdate"
  >
    <div class="sideNavBody">
      <div class="sideNavBrand" :class="{ isCollapsed: isCollapsedBrand }">
        <img v-if="settings.companyLogoUrl" :src="settings.companyLogoUrl" alt="Company logo" class="companyLogo" />
        <div v-if="showCompanyName" class="companyName">{{ company?.name || 'MyCo+' }}</div>
      </div>
      <div v-if="user?.isSuperAdmin && showCompanyName" class="companyAsWrap" data-testid="company-as-switcher">
        <v-select
          v-model="actingSelection"
          :items="companyOptions"
          item-title="name"
          item-value="id"
          density="compact"
          hide-details
          clearable
          placeholder="Viewing company…"
          bg-color="white"
          data-testid="company-as-select"
          :menu-props="companyMenuProps"
          @update:model-value="onActingChange"
          @update:menu="onCompanyMenuToggle"
        >
          <template #prepend-item>
            <div class="companyAsSearch px-3 pb-2" @mousedown.stop @click.stop>
              <v-text-field
                ref="companySearchField"
                v-model="companySearch"
                density="compact"
                hide-details
                placeholder="Search…"
                prepend-inner-icon="mdi-magnify"
                bg-color="white"
                data-testid="company-as-search"
                autocomplete="off"
                @keydown.stop
              />
            </div>
            <v-divider />
          </template>
          <template #item="{ props, item }">
            <v-list-item v-if="companyMatchesSearch(item.raw)" v-bind="props" :title="item.raw.name" />
          </template>
          <template #append-item>
            <div
              v-if="!visibleCompanyCount"
              class="px-3 py-2 text-medium-emphasis text-caption"
              data-testid="company-as-empty"
            >
              No matches
            </div>
          </template>
        </v-select>
      </div>
      <v-list nav density="compact" class="sideNavList">
        <v-list-item prepend-icon="mdi-view-dashboard" title="Dashboard" to="/dashboard" @click="closeMobileNav" />
        <v-list-item
          v-if="user?.isAdmin"
          prepend-icon="mdi-cog"
          title="Settings"
          to="/settings"
          @click="closeMobileNav"
        />
        <v-list-item prepend-icon="mdi-briefcase" title="Jobs" to="/jobs" @click="closeMobileNav" />
        <v-list-item prepend-icon="mdi-form-select" title="Forms" to="/forms" @click="closeMobileNav" />
        <v-list-item prepend-icon="mdi-wrench" title="Maintenance" to="/maintenance" @click="closeMobileNav" />
        <v-list-item prepend-icon="mdi-shield-check" title="Safety" to="/safety" @click="closeMobileNav" />
        <v-list-item prepend-icon="mdi-account-group" title="HR" to="/hr" @click="closeMobileNav" />
        <v-list-item
          v-if="user?.isAdmin"
          prepend-icon="mdi-package-variant"
          title="Inventory"
          to="/inventory"
          @click="closeMobileNav"
        />
        <template v-if="user?.isSuperAdmin">
          <v-divider class="my-2" />
          <v-list-subheader class="platformSubheader">Platform</v-list-subheader>
          <v-list-item
            prepend-icon="mdi-image-multiple"
            title="E2E Testing"
            to="/platform/e2e"
            data-testid="nav-platform-e2e"
            @click="closeMobileNav"
          />
          <v-list-item
            prepend-icon="mdi-chart-timeline-variant"
            title="Usage"
            to="/platform/usage"
            data-testid="nav-platform-usage"
            @click="closeMobileNav"
          />
        </template>
      </v-list>
      <v-list nav density="compact" class="sideNavFooter">
        <v-list-item prepend-icon="mdi-bug" title="Report a Bug" @click="openBugReport" />
      </v-list>
    </div>
    <BugReportModal v-model="bugReportOpen" />
  </v-navigation-drawer>
</template>

<script>
import { useDisplay } from 'vuetify'
import { mapGetters, mapState } from 'vuex'
import BugReportModal from '../modals/BugReportModal.vue'
import PlatformAPI from '../../api/platform'
import { parseCompanyProperties } from '../../utils/companySettings'
import { messageForApiError } from '../../utils/apiErrorMessage'

export default {
  name: 'AppSideNav',
  components: { BugReportModal },
  setup() {
    const { mdAndDown } = useDisplay()
    return { mdAndDown }
  },
  data() {
    return {
      bugReportOpen: false,
      companyOptions: [],
      companySearchLoading: false,
      companySearch: '',
      actingSelection: null,
    }
  },
  computed: {
    ...mapState(['user', 'company', 'navCollapsed', 'navMobileOpen']),
    ...mapGetters(['themeColor', 'companySettings']),
    settings() {
      return this.companySettings
    },
    isMobileLayout() {
      return this.mdAndDown
    },
    drawerVisible() {
      return this.isMobileLayout ? this.navMobileOpen : true
    },
    isCollapsedBrand() {
      return !this.isMobileLayout && this.navCollapsed
    },
    showCompanyName() {
      return this.isMobileLayout || !this.navCollapsed
    },
    companyMenuProps() {
      return {
        minWidth: 360,
        maxWidth: 360,
        width: 360,
        contentClass: 'companyAsMenu',
      }
    },
    visibleCompanyCount() {
      return this.companyOptions.filter((c) => this.companyMatchesSearch(c)).length
    },
  },
  watch: {
    isMobileLayout(isMobile) {
      if (!isMobile) {
        this.$store.commit('setNavMobileOpen', false)
      }
    },
    $route() {
      this.closeMobileNav()
    },
    user: {
      immediate: true,
      handler(user) {
        if (user?.isSuperAdmin) {
          const acting = Number(user.actingCompanyId || 0)
          const home = Number(user.homeCompanyId || user.companyId || 0)
          this.actingSelection = acting || home || null
          if (!this.companyOptions.length) this.loadCompanies('')
        }
      },
    },
    companySearch(q) {
      this.resetCompanyMenuScroll()
      const term = (q || '').trim()
      if (this._searchTimer) clearTimeout(this._searchTimer)
      if (term.length < 2) return
      this._searchTimer = setTimeout(() => this.loadCompanies(term), 250)
    },
  },
  beforeUnmount() {
    if (this._searchTimer) clearTimeout(this._searchTimer)
  },
  methods: {
    onDrawerUpdate(value) {
      if (this.isMobileLayout) {
        this.$store.commit('setNavMobileOpen', value)
      }
    },
    closeMobileNav() {
      if (this.isMobileLayout) {
        this.$store.commit('setNavMobileOpen', false)
      }
    },
    openBugReport() {
      this.bugReportOpen = true
      this.closeMobileNav()
    },
    companyMatchesSearch(company) {
      const q = (this.companySearch || '').trim().toLowerCase()
      if (!q) return true
      if (!company) return false
      return `${company.name || ''} ${company.email || ''}`.toLowerCase().includes(q)
    },
    onCompanyMenuToggle(open) {
      if (!open) return
      this.companySearch = ''
      this.focusCompanySearch()
      this.resetCompanyMenuScroll()
    },
    focusCompanySearch() {
      this.$nextTick(() => {
        const root = document.querySelector('[data-testid="company-as-search"]')
        const input = root?.querySelector?.('input')
        input?.focus()
      })
    },
    resetCompanyMenuScroll() {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const menu = document.querySelector('.companyAsMenu')
            const list = menu?.querySelector?.('.v-list')
            if (menu) menu.scrollTop = 0
            if (list) list.scrollTop = 0
          })
        })
      })
    },
    async loadCompanies(q) {
      if (!this.user?.isSuperAdmin) return
      this.companySearchLoading = true
      const searchActive = Boolean((q || '').trim())
      try {
        const { data } = await PlatformAPI.companies({ q })
        const items = data?.items || []
        if (!q) {
          this.companyOptions = items
        } else {
          // Merge server matches; only replace the array when new ids appear so the
          // open menu / search field are not remounted on every keystroke.
          const byId = new Map(this.companyOptions.map((c) => [c.id, c]))
          let added = false
          for (const item of items) {
            if (!byId.has(item.id)) added = true
            byId.set(item.id, item)
          }
          if (added) this.companyOptions = [...byId.values()]
        }
      } catch {
        if (!q) this.companyOptions = []
      } finally {
        this.companySearchLoading = false
        if (searchActive) this.focusCompanySearch()
      }
    },
    async onActingChange(companyId) {
      const homeId = Number(this.user?.homeCompanyId || 0)
      const selected = companyId == null || companyId === '' ? null : Number(companyId)
      const clearing = selected == null || Number.isNaN(selected) || selected === homeId
      try {
        const { data } = await PlatformAPI.setActingCompany(clearing ? null : selected)
        const nextUser = data?.user || null
        if (nextUser) this.$store.commit('setUser', nextUser)
        if (data?.company) {
          const company = { ...data.company }
          if (typeof company.properties === 'string') {
            company.properties = parseCompanyProperties(company.properties)
          }
          this.$store.commit('setCompany', company)
        }
        await this.$store.dispatch('clearTenantCaches')
        this.actingSelection = clearing ? homeId : selected
        this.companySearch = ''
        if (!this.$route.path.startsWith('/platform')) {
          await this.$router.push('/dashboard')
        }
      } catch (err) {
        this.$store.commit('setSnackbar', {
          type: 'error',
          message: messageForApiError(err, 'Could not switch company.'),
        })
        this.actingSelection = this.user?.actingCompanyId || this.user?.homeCompanyId || null
      }
    },
  },
}
</script>

<style scoped>
.sideNavBody {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.sideNavBrand {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  color: #fff;
  padding: 14px 10px 10px;
  flex-shrink: 0;
}

.sideNavBrand.isCollapsed {
  padding: 10px 6px 8px;
}

.companyLogo {
  display: block;
  margin: 0 auto;
  object-fit: contain;
}

.sideNavBrand:not(.isCollapsed) .companyLogo {
  max-height: 60px;
  max-width: 80%;
}

.sideNavBrand.isCollapsed .companyLogo {
  max-height: 44px;
  max-width: 90%;
}

.companyName {
  margin: 10px auto 5px;
  color: #fff;
  text-align: center;
  font-size: 18px;
  font-weight: 400;
  line-height: 1.25;
  width: 90%;
  word-break: break-word;
}

.companyAsWrap {
  padding: 0 10px 10px;
}

.companyAsSearch {
  position: relative;
  z-index: 1;
}

.platformSubheader {
  color: rgba(255, 255, 255, 0.75) !important;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.sideNavList :deep(.v-list-item-title),
.sideNavList :deep(.v-icon) {
  color: #fff !important;
}

.sideNavList :deep(.v-list-item--active) {
  background: #fff;
}

.sideNavList :deep(.v-list-item--active .v-list-item-title),
.sideNavList :deep(.v-list-item--active .v-icon) {
  color: var(--app-theme-color, #1fe9d3) !important;
}

.sideNavFooter {
  margin-top: auto;
  flex-shrink: 0;
}

.sideNavFooter :deep(.v-list-item-title),
.sideNavFooter :deep(.v-icon) {
  color: #fff !important;
}
</style>

<style>
.sideNav.v-navigation-drawer--rail .sideNavBrand {
  overflow: hidden;
}

/* Teleported overlay — must be unscoped */
.companyAsMenu {
  max-width: 360px !important;
  width: 360px !important;
}

.companyAsMenu .v-list-item-title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
